import { ReactNode, useEffect, useLayoutEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AdminSidebar } from "./AdminSidebar";
import { AdminHeader } from "./AdminHeader";
import {
  applyRestoredScroll,
  consumeAdminNavScrollRestore,
} from "./adminNavScroll";

interface AdminLayoutProps {
  children: ReactNode;
}

/**
 * BrowserRouter does not honor Link preventScrollReset the same way as the data router.
 * Scroll capture/restore lives in adminNavScroll.ts so it survives AdminLayout remounting
 * on each route (each page wraps itself in AdminLayout).
 */
export const AdminLayout = ({ children }: AdminLayoutProps) => {
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useLayoutEffect(() => {
    const restored = consumeAdminNavScrollRestore();
    if (!restored) {
      return;
    }
    applyRestoredScroll(restored.sidebar, restored.main);
  }, [location.pathname]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileNavOpen) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMobileNavOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [mobileNavOpen]);

  return (
    <div className="flex min-h-screen bg-background">
      {mobileNavOpen ? (
        <button
          type="button"
          aria-label="Close navigation menu"
          className="fixed inset-0 z-40 bg-black/50 duration-200 lg:hidden"
          onClick={() => setMobileNavOpen(false)}
        />
      ) : null}

      <AdminSidebar
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />

      <div className="flex min-w-0 flex-1 flex-col overflow-x-hidden lg:ml-64">
        <AdminHeader onMenuClick={() => setMobileNavOpen(true)} />
        {/* Shared gutter + max width so main aligns on all breakpoints (incl. wide screens) */}
        <div className="mx-auto flex w-full min-w-0 max-w-screen-2xl flex-1 flex-col px-4 sm:px-6 lg:px-8 2xl:px-10">
          <main className="flex-1 py-4 sm:py-6 lg:py-8">{children}</main>
        </div>
      </div>
    </div>
  );
};
